import { create } from 'zustand';
import type { Track } from '@pujolisique/shared';

export type RepeatMode = 'off' | 'all' | 'one';
export type Quality = 0 | 320 | 192 | 128;
export type PanelTab = 'queue' | 'lyrics' | 'info';
/** Volume égalisé : désactivé, automatique (album si l'on écoute un album, sinon titre), par titre, par album */
export type NormalizeMode = 'off' | 'auto' | 'track' | 'album';

interface PlayerState {
  queue: Track[];
  /** Ordre d'origine conservé quand l'aléatoire est actif */
  original: Track[] | null;
  index: number;
  playing: boolean;
  currentTime: number;
  duration: number;
  buffering: boolean;
  volume: number;
  muted: boolean;
  shuffle: boolean;
  repeat: RepeatMode;
  quality: Quality;
  /** Nom de la source de lecture (« Album Nocturnes », « Mix Debussy »…) */
  source?: string;
  nowPlayingOpen: boolean;
  panelTab: PanelTab;
  normalize: NormalizeMode;
  /** Fondu enchaîné en secondes (0 = sans) */
  crossfade: number;
  /** Enchaînement sans blanc entre les titres d'un même album */
  gapless: boolean;
  /** Radio infinie : la file se prolonge avec des titres proches */
  radio: boolean;

  playTracks(tracks: Track[], start?: number, opts?: { shuffle?: boolean; source?: string }): void;
  playNext(tracks: Track[]): void;
  addToQueue(tracks: Track[]): void;
  jump(i: number): void;
  next(auto?: boolean): void;
  prev(): void;
  removeAt(i: number): void;
  move(from: number, to: number): void;
  clearUpcoming(): void;
  toggle(): void;
  setPlaying(p: boolean): void;
  seek(t: number): void;
  setVolume(v: number): void;
  toggleMute(): void;
  toggleShuffle(): void;
  cycleRepeat(): void;
  setQuality(q: Quality): void;
  openNowPlaying(open: boolean, tab?: PanelTab): void;
  setPanelTab(tab: PanelTab): void;
  setNormalize(m: NormalizeMode): void;
  setCrossfade(sec: number): void;
  setGapless(on: boolean): void;
  toggleRadio(): void;
}

const STORAGE_KEY = 'pujolisique.player.v2';

/** Le moteur fournit la recherche de position (gère les flux convertis) */
let seekHandler: (t: number) => void = () => undefined;
let replayHandler: () => void = () => undefined;
export function setSeekHandler(fn: (t: number) => void, replay?: () => void) {
  seekHandler = fn;
  if (replay) replayHandler = replay;
}

function load(): Partial<PlayerState> & { position?: number } {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const saved = load();

export const usePlayer = create<PlayerState>((set, get) => ({
  queue: saved.queue ?? [],
  original: saved.original ?? null,
  index: Math.min(saved.index ?? 0, Math.max(0, (saved.queue?.length ?? 1) - 1)),
  playing: false,
  currentTime: saved.position ?? 0,
  duration: 0,
  buffering: false,
  volume: saved.volume ?? 0.9,
  muted: false,
  shuffle: saved.shuffle ?? false,
  repeat: saved.repeat ?? 'off',
  quality: saved.quality ?? 0,
  source: saved.source,
  nowPlayingOpen: false,
  panelTab: 'queue',
  normalize: saved.normalize ?? 'auto',
  crossfade: saved.crossfade ?? 4,
  gapless: saved.gapless ?? true,
  radio: saved.radio ?? false,

  playTracks(tracks, start = 0, opts = {}) {
    if (!tracks.length) return;
    const shuffle = opts.shuffle ?? get().shuffle;
    if (shuffle) {
      const first = tracks[start] ?? tracks[0];
      const rest = shuffled(tracks.filter((_, i) => i !== start));
      set({ queue: [first, ...rest], original: tracks, index: 0, playing: true, shuffle, source: opts.source, currentTime: 0 });
    } else {
      set({ queue: tracks, original: null, index: start, playing: true, shuffle, source: opts.source, currentTime: 0 });
    }
  },
  playNext(tracks) {
    const { queue, index } = get();
    if (!queue.length) return get().playTracks(tracks);
    const q = [...queue];
    q.splice(index + 1, 0, ...tracks);
    set({ queue: q });
  },
  addToQueue(tracks) {
    const { queue } = get();
    if (!queue.length) return get().playTracks(tracks);
    set({ queue: [...queue, ...tracks] });
  },
  jump(i) {
    if (i >= 0 && i < get().queue.length) set({ index: i, playing: true, currentTime: 0 });
  },
  next(auto = false) {
    const { index, queue, repeat } = get();
    if (auto && repeat === 'one') {
      replayHandler();
      return;
    }
    if (index + 1 < queue.length) set({ index: index + 1, playing: true, currentTime: 0 });
    else if (repeat === 'all' && queue.length) set({ index: 0, playing: true, currentTime: 0 });
    else if (auto) set({ playing: false });
  },
  prev() {
    const { index, queue, repeat } = get();
    if (get().currentTime > 3 || (index === 0 && repeat !== 'all')) {
      seekHandler(0);
      set({ currentTime: 0 });
      return;
    }
    set({ index: index > 0 ? index - 1 : queue.length - 1, playing: true, currentTime: 0 });
  },
  removeAt(i) {
    const { queue, index } = get();
    if (i === index) return;
    const q = queue.filter((_, k) => k !== i);
    set({ queue: q, index: i < index ? index - 1 : index });
  },
  move(from, to) {
    const { queue, index } = get();
    if (from === to || from < 0 || to < 0 || from >= queue.length || to >= queue.length) return;
    const q = [...queue];
    const [t] = q.splice(from, 1);
    q.splice(to, 0, t);
    let ni = index;
    if (from === index) ni = to;
    else if (from < index && to >= index) ni = index - 1;
    else if (from > index && to <= index) ni = index + 1;
    set({ queue: q, index: ni });
  },
  clearUpcoming() {
    const { queue, index } = get();
    set({ queue: queue.slice(0, index + 1) });
  },
  toggle() {
    if (!get().queue.length) return;
    set({ playing: !get().playing });
  },
  setPlaying(p) {
    set({ playing: p });
  },
  seek(t) {
    if (!Number.isFinite(t)) return;
    const pos = Math.max(0, Math.min(t, get().duration || Infinity));
    seekHandler(pos);
    set({ currentTime: pos });
  },
  setVolume(v) {
    const vol = Math.min(1, Math.max(0, v));
    set({ volume: vol, muted: vol === 0 ? get().muted : false });
  },
  toggleMute() {
    set({ muted: !get().muted });
  },
  toggleShuffle() {
    const { shuffle, queue, index, original } = get();
    const current = queue[index];
    if (!shuffle) {
      const rest = shuffled(queue.filter((_, i) => i !== index));
      set({ shuffle: true, original: queue, queue: current ? [current, ...rest] : rest, index: 0 });
    } else {
      const base = original ?? queue;
      const ni = current ? Math.max(0, base.findIndex((t) => t.id === current.id)) : 0;
      set({ shuffle: false, original: null, queue: base, index: ni });
    }
  },
  cycleRepeat() {
    const order: RepeatMode[] = ['off', 'all', 'one'];
    set({ repeat: order[(order.indexOf(get().repeat) + 1) % 3] });
  },
  setQuality(q) {
    set({ quality: q });
  },
  openNowPlaying(open, tab) {
    set({ nowPlayingOpen: open, ...(tab ? { panelTab: tab } : {}) });
  },
  setPanelTab(tab) {
    set({ panelTab: tab });
  },
  setNormalize(m) {
    set({ normalize: m });
  },
  setCrossfade(sec) {
    set({ crossfade: Math.max(0, Math.min(12, Math.round(sec))) });
  },
  setGapless(on) {
    set({ gapless: on });
  },
  toggleRadio() {
    set({ radio: !get().radio });
  },
}));

export const currentTrack = (s: PlayerState) => s.queue[s.index] as Track | undefined;

/* Persistance (débit limité) */
let saveTimer: number | undefined;
usePlayer.subscribe((s) => {
  if (saveTimer) return;
  saveTimer = window.setTimeout(() => {
    saveTimer = undefined;
    const st = usePlayer.getState();
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          queue: st.queue.slice(0, 1000),
          original: st.original?.slice(0, 1000) ?? null,
          index: st.index,
          volume: st.volume,
          shuffle: st.shuffle,
          repeat: st.repeat,
          quality: st.quality,
          source: st.source,
          position: Math.floor(st.currentTime),
          normalize: st.normalize,
          crossfade: st.crossfade,
          gapless: st.gapless,
          radio: st.radio,
        }),
      );
    } catch {
      /* stockage indisponible */
    }
  }, 1500);
  void s;
});
